import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';

import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { formatarDinheiro } from '@/nucleo/dinheiro';

import type { DiaApontamento } from '../tipos';
import { formatarDataLonga, formatarDiaCurto } from './statusAteste';

const COR_PONTO: Record<string, string> = {
  CONVENCIONAL: 'bg-primary',
  CRECHE: 'bg-accent',
  ACESSIVEL: 'bg-success',
};

/** Nível 4 do drill-down: dias apontados de uma OS, com detalhe por modalidade. */
export function DrillDias({ dias }: { dias: DiaApontamento[] }) {
  const [diaAberto, setDiaAberto] = useState<string | null>(null);

  if (dias.length === 0) {
    return (
      <div className="py-3 pl-8 text-xs text-muted-foreground">
        Sem apontamentos diários para esta OS.
      </div>
    );
  }

  return (
    <div className="ml-4 my-2 border-l-2 border-primary/30 pl-8">
      <Table>
        <TableHeader>
          <TableRow className="border-b bg-muted/40 hover:bg-muted/40">
            <TableHead className="h-9 w-10" />
            <TableHead className="h-9 w-32 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Dia
            </TableHead>
            <TableHead className="h-9 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Não-Cad
            </TableHead>
            <TableHead className="h-9 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Cadeirantes
            </TableHead>
            <TableHead className="h-9 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Cont. NC
            </TableHead>
            <TableHead className="h-9 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Cont. Cad
            </TableHead>
            <TableHead className="h-9 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Ativ. N-Cad
            </TableHead>
            <TableHead className="h-9 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Ativ. Cad
            </TableHead>
            <TableHead className="h-9 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
              KM Adic.
            </TableHead>
            <TableHead className="h-9 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dias.map((dia) => {
            const aberto = diaAberto === dia.id;
            const totalModalidades = dia.modalidades.reduce((acc, m) => acc + m.qtd, 0);
            const valorDia = dia.modalidades.reduce((acc, m) => acc + m.valor, 0);

            return (
              <Fragment key={dia.id}>
                <TableRow className="text-xs">
                  <TableCell className="w-10">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setDiaAberto(aberto ? null : dia.id)}
                      disabled={dia.modalidades.length === 0}
                      aria-label="Expandir modalidades do dia"
                    >
                      {aberto ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="font-mono">{formatarDiaCurto(dia.dataOperacao)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {dia.alunosNaoCadeirantes}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{dia.alunosCadeirantes}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {dia.ativContinuaNaoCad}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{dia.ativContinuaCad}</TableCell>
                  <TableCell className="text-right tabular-nums">{dia.ativComplNaoCad}</TableCell>
                  <TableCell className="text-right tabular-nums">{dia.ativComplCad}</TableCell>
                  <TableCell className="text-right tabular-nums">{dia.kmAdicional}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {dia.status}
                    </Badge>
                  </TableCell>
                </TableRow>
                {aberto && (
                  <TableRow className="bg-muted/10 hover:bg-muted/10">
                    <TableCell colSpan={10} className="border-l-2 border-accent/60 p-0">
                      <div className="px-10 py-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/15 text-accent">
                              <Layers className="h-3.5 w-3.5" />
                            </div>
                            <div className="flex flex-col leading-tight">
                              <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
                                Distribuição por modalidade
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                {dia.modalidades.length} modalidade
                                {dia.modalidades.length === 1 ? '' : 's'} ·{' '}
                                {formatarDataLonga(dia.dataOperacao)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                Alunos
                              </span>
                              <span className="font-semibold tabular-nums text-foreground">
                                {totalModalidades}
                              </span>
                            </div>
                            <div className="h-3 w-px bg-border" />
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                Valor
                              </span>
                              <span className="font-mono text-sm font-semibold text-foreground">
                                {formatarDinheiro(valorDia)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="overflow-hidden rounded-lg border border-border bg-card">
                          <div className="grid grid-cols-[16px_minmax(140px,1.4fr)_minmax(120px,1fr)_90px_120px_minmax(80px,100px)] items-center gap-3 border-b border-border bg-muted/50 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            <span />
                            <span>Modalidade</span>
                            <span>Sublinha</span>
                            <span className="text-right">Alunos</span>
                            <span>Participação</span>
                            <span className="text-right">Valor</span>
                          </div>
                          <div className="divide-y divide-border/60">
                            {dia.modalidades.map((modalidade, i) => {
                              const pctAlunos =
                                totalModalidades > 0
                                  ? (modalidade.qtd / totalModalidades) * 100
                                  : 0;
                              const pctValor =
                                valorDia > 0 ? (modalidade.valor / valorDia) * 100 : 0;
                              const corPonto =
                                COR_PONTO[modalidade.modalidade] ?? 'bg-muted-foreground';
                              return (
                                <div
                                  key={`${modalidade.modalidade}-${modalidade.sublinha}-${i}`}
                                  className="group grid grid-cols-[16px_minmax(140px,1.4fr)_minmax(120px,1fr)_90px_120px_minmax(80px,100px)] items-center gap-3 px-3 py-2 text-xs transition-colors hover:bg-muted/30"
                                >
                                  <span
                                    className={`h-2 w-2 rounded-full ${corPonto}`}
                                    aria-hidden
                                  />
                                  <span className="truncate font-medium text-foreground">
                                    {modalidade.modalidade}
                                  </span>
                                  <span className="truncate text-muted-foreground">
                                    {modalidade.sublinha}
                                  </span>
                                  <span className="text-right font-medium tabular-nums text-foreground">
                                    {modalidade.qtd}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                                      <div
                                        className={`absolute inset-y-0 left-0 rounded-full opacity-80 ${corPonto}`}
                                        style={{ width: `${pctValor}%` }}
                                      />
                                    </div>
                                    <span className="w-9 text-right text-[10px] tabular-nums text-muted-foreground">
                                      {pctAlunos.toFixed(0)}%
                                    </span>
                                  </div>
                                  <span className="text-right font-mono tabular-nums text-foreground">
                                    {formatarDinheiro(modalidade.valor)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          <div className="grid grid-cols-[16px_minmax(140px,1.4fr)_minmax(120px,1fr)_90px_120px_minmax(80px,100px)] items-center gap-3 border-t border-border bg-muted/30 px-3 py-2 text-xs font-semibold">
                            <span />
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              Total do dia
                            </span>
                            <span />
                            <span className="text-right tabular-nums text-foreground">
                              {totalModalidades}
                            </span>
                            <span className="text-[10px] text-muted-foreground">100%</span>
                            <span className="text-right font-mono tabular-nums text-foreground">
                              {formatarDinheiro(valorDia)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
